import React from 'react';
import { motion } from 'framer-motion';
import {
  Cog,
  Droplet,
  Settings,
  Link2,
  Zap,
  Wrench,
  Filter,
  Fuel,
  Fan,
  Gauge,
  Construction,
  PanelTop,
  Truck,
  GitMerge,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  categoryPath,
  getSystemCategories,
  getBrandCategories,
} from '../data/productCategories';
import { getItemsForBrand } from '../data/productItems';

const CATEGORY_ICON_MAP = {
  Cog,
  Droplet,
  Settings,
  Link2,
  Zap,
  Wrench,
  Filter,
  Fuel,
  Fan,
  Gauge,
  Construction,
  PanelTop,
  Truck,
  GitMerge,
};

const ProductCategories = () => {
  const navigate = useNavigate();
  const systemCategories = getSystemCategories();
  const brandCategories = getBrandCategories();

  const handleViewCategory = (slug) => {
    navigate(categoryPath(slug));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      id="products"
      aria-labelledby="product-categories-heading"
      className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-[#1a1a1a] to-[#2d2d2d]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2
            id="product-categories-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Heavy earth mover spare parts
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            OEM-grade categories for excavators, bulldozers, wheel loaders, motor graders, and ADTs —
            same industrial quality, organized for faster sourcing and clearer machine coverage.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {systemCategories.map((category) => {
            const IconComponent = CATEGORY_ICON_MAP[category.iconName] ?? Cog;
            return (
              <motion.article
                key={category.slug}
                variants={itemVariants}
                whileHover={{ scale: 1.03, y: -5 }}
                className="bg-[#2d2d2d] rounded-xl p-6 lg:p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-[#ff6b35]/20 border border-[#4a4a4a]/30 group flex flex-col"
              >
                <div
                  className="w-14 h-14 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: `${category.color}20` }}
                  aria-hidden="true"
                >
                  <IconComponent size={28} style={{ color: category.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#ff6b35] transition-colors duration-300">
                  {category.title}
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed flex-grow">{category.shortDescription}</p>
                <Button
                  type="button"
                  onClick={() => handleViewCategory(category.slug)}
                  variant="outline"
                  className="w-full border-[#ff6b35] text-[#ff6b35] hover:bg-[#ff6b35] hover:text-white transition-all duration-300 mt-auto"
                >
                  View category
                </Button>
              </motion.article>
            );
          })}
        </motion.div>

        {brandCategories.length > 0 && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mt-20 mb-12 lg:mb-16"
            >
              <h2
                id="brand-categories-heading"
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
              >
                Shop by machine brand
              </h2>
              <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                LiuGong, SDLG, XCMG wheel loader parts and ZF powershift transmission spares —
                browse by your machine to see SKU-level availability and direct-fit replacements.
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              aria-labelledby="brand-categories-heading"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
            >
              {brandCategories.map((category) => {
                const IconComponent = CATEGORY_ICON_MAP[category.iconName] ?? Truck;
                const itemCount = getItemsForBrand(category.slug).length;
                return (
                  <motion.article
                    key={category.slug}
                    variants={itemVariants}
                    whileHover={{ scale: 1.03, y: -5 }}
                    className="bg-[#2d2d2d] rounded-xl p-6 lg:p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-[#ff6b35]/20 border border-[#4a4a4a]/30 group flex flex-col"
                  >
                    <div
                      className="w-14 h-14 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                      style={{ backgroundColor: `${category.color}20` }}
                      aria-hidden="true"
                    >
                      <IconComponent size={28} style={{ color: category.color }} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#ff6b35] transition-colors duration-300">
                      {category.title}
                    </h3>
                    {itemCount > 0 && (
                      <p className="text-xs uppercase tracking-wider text-[#ff6b35] font-semibold mb-3">
                        {itemCount} part{itemCount === 1 ? '' : 's'} listed
                      </p>
                    )}
                    <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
                      {category.shortDescription}
                    </p>
                    <Button
                      type="button"
                      onClick={() => handleViewCategory(category.slug)}
                      variant="outline"
                      className="w-full border-[#ff6b35] text-[#ff6b35] hover:bg-[#ff6b35] hover:text-white transition-all duration-300 mt-auto"
                    >
                      Browse {category.brandLabel || 'parts'}
                    </Button>
                  </motion.article>
                );
              })}
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
};

export default ProductCategories;
